import { useState, useEffect, useRef } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { handleProjectCreateError } from '@/store/project-create-error'
import { CogoIframe, type CogoIframeRef } from '@/components/cogo-iframe'
import {
  ArrowLeft,
  Monitor,
  Tablet,
  Smartphone,
  FileText,
  Layers,
  Play,
  Plus,
  TestTube2,
  ChevronRight,
  Target,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { TEMPLATE_STORIES } from '@/features/story/data'
import { STARTER_BASE } from '@cogo/constants'
import { openStudioLaunchChat } from '@/features/studio-dashboard/lib/studio-launch-chat'

// ---------------------------------------------------------------------------
// Interaction scenario types and loader.
// ---------------------------------------------------------------------------

interface BddThenStep {
  type: 'update' | 'show' | 'navigate' | 'assert'
  target?: string
  action?: string
  route?: string
  description: string
}

interface BddScenario {
  name: string
  tags?: string[]
  given: Array<{ description: string; state?: string; hasValue?: boolean }>
  when: {
    description: string
    element?: string
    event?: string
    flow?: string
  }
  then: BddThenStep[]
}

interface BddBlock {
  blockId: string
  feature: string
  scenarios: BddScenario[]
}

async function loadBddScenarios(
  templateId: string,
  _blocks: string[],
): Promise<BddBlock[]> {
  try {
    const url = `/apps/${templateId}/app.spec.json`
    const res = await fetch(url)
    if (!res.ok) return []
    const ct = res.headers.get('content-type') ?? ''
    if (!ct.includes('json')) return []
    const spec = await res.json()
    const scenarios = spec?.scenarios as Array<{
      id?: string
      name: string
      given: string | Array<{ description: string; state?: string; hasValue?: boolean }>
      when: { action?: string; target?: string; description?: string; params?: Record<string, unknown> }
      then: Array<{ assert?: string; type?: string; description?: string; [k: string]: unknown }>
    }> | undefined
    if (!scenarios?.length) return []

    // Convert app.spec.json scenarios into BddBlock format.
    // Group all scenarios under a single "app" block since they are app-level.
    const converted: BddScenario[] = scenarios.map((s) => ({
      name: s.name,
      tags: [],
      given: typeof s.given === 'string'
        ? [{ description: s.given }]
        : (s.given ?? []),
      when: {
        description: s.when.description ?? `${s.when.action ?? 'interact'} on ${s.when.target ?? 'element'}`,
        element: s.when.target,
        event: s.when.action,
      },
      then: (s.then ?? []).map((t) => ({
        type: (t.type ?? t.assert ?? 'assert') as BddThenStep['type'],
        description: t.description ?? formatAssert(t),
      })),
    }))

    return [{
      blockId: templateId,
      feature: spec.name ?? templateId,
      scenarios: converted,
    }]
  } catch {
    return []
  }
}

/** Format an assert object from app.spec.json into a readable description */
function formatAssert(t: Record<string, unknown>): string {
  if (t.description) return String(t.description)
  const assert = t.assert as string | undefined
  if (assert === 'elements-visible') return `${t.selector} visible (min: ${t.minCount ?? 1})`
  if (assert === 'text-contains') return `${t.selector} contains "${t.pattern}"`
  if (assert === 'state-changed') return `${t.path} ${t.condition}`
  if (assert === 'page-rendered') return `page ${t.pageId} rendered`
  if (assert === 'toast-shown') return `toast: "${t.message}"`
  return assert ?? 'check'
}

// ---------------------------------------------------------------------------
// Scenario tag badge.
// ---------------------------------------------------------------------------

function TagBadge({ tag }: { tag: string }) {
  const isHappy = tag.includes('happy')
  const isError = tag.includes('error')
  return (
    <span
      className={cn(
        'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
        isHappy && 'bg-primary/10 text-primary',
        isError && 'bg-red-500/10 text-red-600',
        !isHappy && !isError && 'bg-muted text-muted-foreground',
      )}
    >
      {tag}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Then step icon
// ---------------------------------------------------------------------------

function ThenIcon({ type }: { type: string }) {
  if (type === 'update') return <span className="text-blue-500 text-[10px]">STATE</span>
  if (type === 'show') return <span className="text-amber-500 text-[10px]">SHOW</span>
  if (type === 'navigate') return <span className="text-purple-500 text-[10px]">NAV</span>
  return <span className="text-muted-foreground text-[10px]">CHECK</span>
}

// ---------------------------------------------------------------------------
// Preview URL mapping — inverted from cogo-starter's TEMPLATE_MAP
// ---------------------------------------------------------------------------

function getPreviewUrl(templateId: string | null): string | null {
  if (!templateId) return null
  return `${STARTER_BASE}/templates/${templateId}`
}

// ---------------------------------------------------------------------------
// Device presets
// ---------------------------------------------------------------------------

type Device = 'desktop' | 'tablet' | 'mobile'

const DEVICE_WIDTHS: Record<Device, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
}

const DEVICE_ICONS: Record<Device, typeof Monitor> = {
  desktop: Monitor,
  tablet: Tablet,
  mobile: Smartphone,
}

// ---------------------------------------------------------------------------
// TemplateDetailPage
// ---------------------------------------------------------------------------

export function TemplateDetailPage() {
  const templateId = useStudioStore((s) => s.detailTemplateId)
  const projectList = useStudioStore((s) => s.projectList)
  const closeDetail = useStudioStore((s) => s.closeTemplateDetail)
  const openProject = useStudioStore((s) => s.openProject)
  const isAdmin = useStudioStore((s) => s.isAdmin)
  const createServerProject = useStudioStore((s) => s.createServerProject)

  // Go back to where the user came from (homepage, templates listing, or dashboard)
  const handleBack = () => {
    const currentPath = window.location.pathname
    const isFromHomepage = currentPath === '/' || currentPath === '/homepage'
    const isFromTemplateDetail = currentPath.startsWith('/templates/')

    closeDetail()

    if (isFromHomepage) {
      // Came from homepage — no URL change happened (openTemplateDetail only sets view),
      // so just closing detail is enough. Force route state sync.
      window.dispatchEvent(new PopStateEvent('popstate'))
    } else if (isFromTemplateDetail) {
      // Direct URL entry to /templates/:id — navigate to templates listing
      window.history.replaceState(null, '', '/templates')
      window.dispatchEvent(new PopStateEvent('popstate'))
    } else if (window.history.length > 1) {
      window.history.back()
    } else {
      window.history.replaceState(null, '', '/')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  const template = projectList.find((t) => t.id === templateId)

  // persona-ux-04 — was fire-and-forget at both template create buttons: a
  // rejected create swallowed its rejection (no signOut on auth expiry).
  // Route through the shared handler so expiry signs out and an unmarked error
  // surfaces, without double-toasting the producer's marked toast.
  const handleUseTemplate = () => {
    const name = template?.name ?? 'New App'
    void createServerProject(name, templateId ?? undefined)
      .then(() => openStudioLaunchChat({
        source: 'template',
        projectName: name,
        templateName: template?.name,
      }))
      .catch((err) =>
        handleProjectCreateError(
          err,
          {
            toastError: (m) => toast.error(m),
            signOut: () => void useStudioStore.getState().signOut(),
          },
          'Failed to create from template',
        ),
      )
  }

  const story = templateId ? TEMPLATE_STORIES[templateId] : undefined
  const previewUrl = getPreviewUrl(templateId)
  const [device, setDevice] = useState<Device>('desktop')
  const iframeRef = useRef<CogoIframeRef>(null)
  const [bddBlocks, setBddBlocks] = useState<BddBlock[]>([])
  const [bddLoading, setBddLoading] = useState(false)
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null)

  useEffect(() => {
    if (!template) return
    setBddLoading(true)
    loadBddScenarios(template.id, template.blocks).then((data) => {
      setBddBlocks(data)
      setBddLoading(false)
    })
  }, [template?.id])

  // Unified scenario data: prefer story scenarios, fallback to block-level scenarios.
  const storyScenarios = story?.bddScenarios ?? []
  const blockScenarioCount = bddBlocks.reduce((s, b) => s + b.scenarios.length, 0)
  const totalScenarios = storyScenarios.length > 0 ? storyScenarios.length : blockScenarioCount

  if (!template) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Template not found</p>
          <button
            onClick={handleBack}
            className="text-sm text-primary hover:underline"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border h-12 px-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="w-px h-5 bg-border" />
          <h1 className="text-sm font-semibold">{template.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => {
                if (templateId) openProject(templateId)
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Play className="w-3 h-3" />
              Open in Editor
            </button>
          )}
          <button
            onClick={handleUseTemplate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-primary text-primary-foreground hover:opacity-90 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Create
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — Story-First layout */}
        <aside className="w-72 border-r border-border overflow-y-auto shrink-0 flex flex-col">
          <div className="p-4 space-y-5 flex-1">
            {/* Narrative */}
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                Narrative
              </h3>
              <p className="text-sm text-foreground leading-relaxed">
                {story?.narrative ?? template.description}
              </p>
            </div>

            {/* Goals */}
            {story && story.goals.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Goals
                </h3>
                <ul className="space-y-1.5">
                  {story.goals.map((goal, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <Target className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Stats bar */}
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FileText className="w-3.5 h-3.5" />
                <span>{template.pages} pages</span>
              </div>
              <div className="w-px h-3.5 bg-border" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Layers className="w-3.5 h-3.5" />
                <span>{template.blocks.length} blocks</span>
              </div>
              {totalScenarios > 0 && (
                <>
                  <div className="w-px h-3.5 bg-border" />
                  <div className="flex items-center gap-1.5 text-xs text-primary">
                    <TestTube2 className="w-3.5 h-3.5" />
                    <span>{totalScenarios} sc</span>
                  </div>
                </>
              )}
            </div>

            {/* Interaction Scenarios — Story-level (action and assertion) */}
            {storyScenarios.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Interaction Scenarios ({storyScenarios.length})
                </h3>
                <div className="space-y-1.5">
                  {storyScenarios.map((scenario, idx) => (
                    <div key={idx} className="rounded-lg border border-border bg-card/50">
                      <button
                        onClick={() =>
                          setExpandedBlock(expandedBlock === scenario.id ? null : scenario.id)
                        }
                        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:bg-accent/50 transition-colors rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronRight
                            className={cn(
                              'w-3 h-3 text-muted-foreground transition-transform shrink-0',
                              expandedBlock === scenario.id && 'rotate-90',
                            )}
                          />
                          <span className="text-xs font-medium text-foreground truncate">
                            {scenario.name}
                          </span>
                        </div>
                      </button>
                      {expandedBlock === scenario.id && (
                        <div className="border-t border-border px-2.5 py-2">
                          <div className="space-y-0.5 text-[11px] font-mono leading-relaxed">
                            {scenario.steps.map((step, i) => (
                              <p key={i} className="text-muted-foreground">
                                <span className={cn(
                                  'font-semibold',
                                  step.type === 'given' && 'text-blue-500',
                                  step.type === 'when' && 'text-amber-500',
                                  step.type === 'then' && 'text-primary',
                                  step.type === 'and' && 'text-muted-foreground',
                                  step.type === 'but' && 'text-red-400',
                                )}>
                                  {step.type.charAt(0).toUpperCase() + step.type.slice(1)}
                                </span>{' '}
                                {step.text}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Block-level scenarios from legacy fixture files, only when no story scenarios. */}
            {!bddLoading && bddBlocks.length > 0 && storyScenarios.length === 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Scenarios ({bddBlocks.reduce((s, b) => s + b.scenarios.length, 0)})
                </h3>
                <div className="space-y-1.5">
                  {bddBlocks.map((block) => (
                    <div key={block.blockId} className="rounded-lg border border-border bg-card/50">
                      <button
                        onClick={() =>
                          setExpandedBlock(expandedBlock === block.blockId ? null : block.blockId)
                        }
                        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:bg-accent/50 transition-colors rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronRight
                            className={cn(
                              'w-3 h-3 text-muted-foreground transition-transform shrink-0',
                              expandedBlock === block.blockId && 'rotate-90',
                            )}
                          />
                          <span className="text-xs font-medium text-foreground truncate">
                            {block.blockId}
                          </span>
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                          {block.scenarios.length}
                        </span>
                      </button>
                      {expandedBlock === block.blockId && (
                        <div className="border-t border-border px-2.5 py-2 space-y-2">
                          {block.scenarios.map((scenario, idx) => (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[11px] font-semibold text-foreground">
                                  {scenario.name}
                                </span>
                                {scenario.tags?.map((tag) => (
                                  <TagBadge key={tag} tag={tag} />
                                ))}
                              </div>
                              <div className="space-y-0.5 text-[11px] font-mono leading-relaxed">
                                {scenario.given.map((g, i) => (
                                  <p key={`g${i}`} className="text-muted-foreground">
                                    <span className="text-blue-500 font-semibold">
                                      {i === 0 ? 'Given' : '  And'}
                                    </span>{' '}
                                    {g.description}
                                  </p>
                                ))}
                                <p className="text-muted-foreground">
                                  <span className="text-amber-500 font-semibold"> When</span>{' '}
                                  {scenario.when.description}
                                </p>
                                {scenario.then.map((t, i) => (
                                  <p
                                    key={`t${i}`}
                                    className="text-muted-foreground flex items-start gap-1"
                                  >
                                    <span className="text-primary font-semibold shrink-0">
                                      {i === 0 ? ' Then' : '  And'}
                                    </span>
                                    <ThenIcon type={t.type} />
                                    <span>{t.description}</span>
                                  </p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sticky bottom CTA */}
          <div className="p-4 border-t border-border space-y-2 shrink-0">
            {isAdmin && (
              <button
                onClick={() => {
                  if (templateId) openProject(templateId)
                }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <Play className="w-3 h-3" />
                Open in Editor
              </button>
            )}
            <button
              onClick={handleUseTemplate}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-primary text-primary-foreground hover:opacity-90 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Create
            </button>
          </div>
        </aside>

        {/* Preview area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Device toggle bar */}
          <div className="flex items-center justify-center gap-1 py-2 border-b border-border shrink-0">
            {(Object.keys(DEVICE_WIDTHS) as Device[]).map((d) => {
              const Icon = DEVICE_ICONS[d]
              return (
                <button
                  key={d}
                  onClick={() => setDevice(d)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors',
                    device === d
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              )
            })}
          </div>

          {/* iframe container */}
          <div className="flex-1 flex items-start justify-center p-4 bg-muted/30 overflow-auto">
            <div
              style={{ width: DEVICE_WIDTHS[device] }}
              className={cn(
                'h-full transition-all duration-300',
                device !== 'desktop' && 'max-w-full',
              )}
            >
              {previewUrl ? (
                <CogoIframe
                  ref={iframeRef}
                  src={previewUrl}
                  className="w-full h-full border-0 rounded-xl shadow-lg bg-white"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center rounded-xl border-2 border-dashed border-border">
                  <div className="text-center space-y-2">
                    <Monitor className="w-10 h-10 text-muted-foreground/30 mx-auto" />
                    <p className="text-sm text-muted-foreground">
                      Preview not available
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      Start cogo-starter on port 3001 to enable live preview
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
